import { isExecTable, isUserTable, extractUserFolder } from './tables'

/** Everything needed to work out where an Edit Data overlay save should land (#968 Part B). */
export interface DashboardSaveContext {
  dashboardName: string
  dashboardId: string
  userEmail: string
}

export interface TableSaveRules {
  /**
   * Folder segment (not a full path) that a fork or a brand-new table lands
   * in: Tables/User/<email>/<targetFolder>. Named for the dashboard so every
   * table saved from this widget collects in one place.
   */
  targetFolder: string
  /**
   * Folder segment the table being edited already lives in, when it is the
   * user's own. Overwrite writes back here — never into targetFolder, which
   * would silently move an unrelated table into this dashboard's folder.
   */
  originalFolder: string
  /** The table being edited is an Exec/company table. Never mutated directly. */
  isExecSource: boolean
  /** The table being edited already belongs to the current user (and isn't Exec). */
  isOwnTable: boolean
  /**
   * Overwrite-in-place is offered only for the user's own tables. Exec
   * tables, another user's tables, and brand-new tables always fork/create
   * into targetFolder.
   */
  allowOverwrite: boolean
}

const MAX_SLUG_LENGTH = 60
const SHORT_ID_LENGTH = 8

/** Lowercases and hyphenates a name for use as a blob-storage path segment. */
export function slugifyForPath(input: string): string {
  const slug = (input || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, '')
  return slug || 'dashboard'
}

/** Short, stable disambiguator for two dashboards that slugify to the same name. */
export function shortDashboardId(dashboardId: string): string {
  return (dashboardId || '').replace(/-/g, '').slice(0, SHORT_ID_LENGTH)
}

/**
 * The folder segment a dashboard's Edit Data saves collect in, e.g.
 * "q3-brand-tracker-a1b2c3d4". Callers prefix this with `Tables/User/<email>/`
 * to get the full path, or pass it bare as the backend's `userFolder` param.
 */
export function dashboardFolderSegment(dashboardName: string, dashboardId: string): string {
  const slug = slugifyForPath(dashboardName)
  const shortId = shortDashboardId(dashboardId)
  return shortId ? `${slug}-${shortId}` : slug
}

/** Full display path for a dashboard's table folder, e.g. for UI copy. */
export function dashboardTableFolderPath(dashboardName: string, dashboardId: string, userEmail: string): string {
  return `Tables/User/${userEmail}/${dashboardFolderSegment(dashboardName, dashboardId)}`
}

/**
 * The fork-vs-overwrite decision for #968 Part B:
 * - Exec/company tables and tables that aren't the current user's always
 *   fork into the dashboard's folder — never mutated in place.
 * - The user's own tables offer overwrite (back into their existing folder)
 *   or fork (into the dashboard's folder).
 * - A brand-new table (originalPath === '') has nothing to overwrite, so it
 *   always creates into the dashboard's folder.
 */
export function resolveTableSaveRules(originalPath: string, ctx: DashboardSaveContext): TableSaveRules {
  const isExecSource = isExecTable(originalPath)
  const isOwnTable = !isExecSource && isUserTable(originalPath, ctx.userEmail)
  return {
    targetFolder: dashboardFolderSegment(ctx.dashboardName, ctx.dashboardId),
    originalFolder: isOwnTable ? extractUserFolder(originalPath, ctx.userEmail) : '',
    isExecSource,
    isOwnTable,
    allowOverwrite: isOwnTable,
  }
}
